import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  TSE_ENVIRONMENT_OPTIONS,
  TSE_PROVIDER_OPTIONS,
  type TseEnvironment,
  type TseProviderKind,
  type TseSettings,
  providerNeedsApiKey,
  providerNeedsApiSecret,
  providerNeedsClientId,
  providerNeedsTssId,
} from "@/lib/tseSettings";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

interface TenantSettings {
  tse: TseSettings;
}

interface TseIntegrationPanelProps {
  canWrite: boolean;
}

export function TseIntegrationPanel({ canWrite }: TseIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tseEnabled, setTseEnabled] = useState(false);
  const [provider, setProvider] = useState<TseProviderKind>("stub");
  const [environment, setEnvironment] = useState<TseEnvironment>("test");
  const [tssId, setTssId] = useState("");
  const [clientId, setClientId] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiSecretConfigured, setApiSecretConfigured] = useState(false);
  const [configured, setConfigured] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.get<TenantSettings>("/api/v1/settings");
      const tse = settings.tse;
      setTseEnabled(tse.enabled);
      setProvider(tse.provider);
      setEnvironment(tse.environment);
      setTssId(tse.tss_id ?? "");
      setClientId(tse.client_id ?? "");
      setBaseUrl(tse.base_url ?? "");
      setApiKey("");
      setApiSecret("");
      setApiKeyConfigured(tse.api_key_configured);
      setApiSecretConfigured(tse.api_secret_configured);
      setConfigured(tse.configured);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedProvider = TSE_PROVIDER_OPTIONS.find((p) => p.value === provider);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        enabled: tseEnabled,
        provider,
        environment,
        tss_id: tssId.trim() || null,
        client_id: clientId.trim() || null,
        base_url: baseUrl.trim() || null,
      };
      if (apiKey) body.api_key = apiKey;
      if (apiSecret) body.api_secret = apiSecret;

      const updated = await api.patch<TenantSettings>("/api/v1/settings/tse", body);
      const tse = updated.tse;
      setApiKey("");
      setApiSecret("");
      setApiKeyConfigured(tse.api_key_configured);
      setApiSecretConfigured(tse.api_secret_configured);
      setConfigured(tse.configured);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "TSE konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
      <FormHint>
        Registrieren Sie hier einen Online-TSE-Anbieter für die gesetzliche Kassensicherung
        (Deutschland). Bar- und Kartenzahlungen werden bei aktiver TSE automatisch signiert.{" "}
        <a
          href="https://github.com/campingplatz-mainkur/campdesk/blob/main/docs/compliance-tse.md"
          target="_blank"
          rel="noreferrer"
          className="text-accent underline"
        >
          Weitere Informationen
        </a>
      </FormHint>

      <label className="flex items-center gap-3 rounded-xl border border-separator bg-fill-quaternary p-4 text-sm">
        <input
          type="checkbox"
          className="rounded border-separator text-accent focus:ring-accent"
          checked={tseEnabled}
          onChange={(e) => setTseEnabled(e.target.checked)}
          disabled={!canWrite}
        />
        <span className="font-medium text-label">TSE-Signierung aktivieren</span>
      </label>

      {tseEnabled && configured ? (
        <p className="rounded-xl border border-status-green/30 bg-status-green/10 px-4 py-3 text-sm text-status-green">
          TSE ist konfiguriert ({selectedProvider?.label ?? provider}).
        </p>
      ) : null}

      {tseEnabled && !configured && provider !== "stub" ? (
        <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          Bitte API-Zugangsdaten und TSS-ID ergänzen, damit die TSE einsatzbereit ist.
        </p>
      ) : null}

      <fieldset className="space-y-4" disabled={!canWrite}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1.5 block font-medium text-label">Anbieter</span>
            <select
              className="eh-input"
              value={provider}
              onChange={(e) => setProvider(e.target.value as TseProviderKind)}
            >
              {TSE_PROVIDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1.5 block font-medium text-label">Umgebung</span>
            <select
              className="eh-input"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as TseEnvironment)}
            >
              {TSE_ENVIRONMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {selectedProvider ? (
          <p className="text-sm text-label-secondary">{selectedProvider.hint}</p>
        ) : null}

        {provider !== "stub" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {providerNeedsApiKey(provider) ? (
              <label className="text-sm">
                <span className="mb-1.5 block text-label-secondary">
                  API-Key{apiKeyConfigured ? " (gespeichert)" : ""}
                </span>
                <input
                  type="password"
                  className="eh-input"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={apiKeyConfigured ? "Leer lassen = unverändert" : ""}
                  autoComplete="new-password"
                />
              </label>
            ) : null}
            {providerNeedsApiSecret(provider) ? (
              <label className="text-sm">
                <span className="mb-1.5 block text-label-secondary">
                  API-Secret{apiSecretConfigured ? " (gespeichert)" : ""}
                </span>
                <input
                  type="password"
                  className="eh-input"
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder={apiSecretConfigured ? "Leer lassen = unverändert" : ""}
                  autoComplete="new-password"
                />
              </label>
            ) : null}
            {providerNeedsTssId(provider) ? (
              <label className="text-sm">
                <span className="mb-1.5 block text-label-secondary">TSS-ID</span>
                <input
                  className="eh-input"
                  value={tssId}
                  onChange={(e) => setTssId(e.target.value)}
                  placeholder="Technische Sicherheitseinrichtung"
                />
              </label>
            ) : null}
            {providerNeedsClientId(provider) ? (
              <label className="text-sm">
                <span className="mb-1.5 block text-label-secondary">Client-ID (optional)</span>
                <input
                  className="eh-input"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="fiskaly Client"
                />
              </label>
            ) : null}
            <label className="text-sm sm:col-span-2">
              <span className="mb-1.5 block text-label-secondary">API-Basis-URL (optional)</span>
              <input
                className="eh-input"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="Standard-URL des Anbieters, falls abweichend"
              />
            </label>
          </div>
        ) : null}

        {provider === "fiskaly" || provider === "swissbit_cloud" ? (
          <p className="text-sm text-label-secondary">
            Die Live-Anbindung an {selectedProvider?.label} folgt in einem späteren Schritt.
            Zugangsdaten können bereits hinterlegt werden. Für Tests nutzen Sie den Stub-Anbieter.
          </p>
        ) : null}
      </fieldset>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">TSE-Einstellungen gespeichert.</p> : null}
      {canWrite ? (
        <div className="flex justify-end border-t border-separator pt-4">
          <Button type="submit" loading={saving}>
            TSE speichern
          </Button>
        </div>
      ) : null}
    </form>
  );
}
